import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Modal } from '@/components/ui/modal';
import type { PartyMember, PartyInfoMeta } from '@/types/party';
import { Search, Zap, MessageSquare, RefreshCw } from 'lucide-react';
import { xboxApiService } from '@/services/xboxApi';
import { useToast } from '@/components/ui/toast';

// Session-level in-memory cache that resets on full page reload
const sessionPartyStateRef: React.MutableRefObject<{
  partyId: string | null;
  meta: PartyInfoMeta;
  members: PartyMember[];
} | null> = { current: null } as any;

const PartyChats: React.FC = () => {
  const [partyMembers, setPartyMembers] = useState<PartyMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [partyId, setPartyId] = useState<string | null>(null);
  const [meta, setMeta] = useState<PartyInfoMeta>({ partyId: null, visibility: 'unknown', memberCount: 0 });
  const { toast, Toaster } = useToast();
  const [friends, setFriends] = useState<Array<{ gamertag: string; xuid: string; isOnline: boolean }>>([]);
  const [selectedText, setSelectedText] = useState<string>('');
  const [friendsModalOpen, setFriendsModalOpen] = useState<boolean>(false);
  // XUID used by crash payload - exclude from lists on errors
  const CRASH_XUID = '2535472147612285';

  // Load friend options from cache only (no API here)
  useEffect(() => {
    const normalizeFromCache = () => {
      const cached = localStorage.getItem('friends_cache');
      if (!cached) return;
      try {
        const parsed = JSON.parse(cached) as Array<any>;
        const list = parsed
          .filter(p => p && (p.xuid || p.gamertag || p.displayName))
          .map(p => ({
            gamertag: p.gamertag || p.displayName,
            xuid: String(p.xuid || ''),
            isOnline: ((p.presenceState || '').toLowerCase() === 'online') || !!p.isOnline,
          }))
          .sort((a: { gamertag: string; isOnline: boolean }, b: { gamertag: string; isOnline: boolean }) =>
            Number(b.isOnline) - Number(a.isOnline) || a.gamertag.localeCompare(b.gamertag)
          );
        setFriends(list);
      } catch {}
    };
    normalizeFromCache();
    const id = window.setInterval(normalizeFromCache, 10000);
    return () => clearInterval(id);
  }, []);

  // Hydrate from in-memory session ref (persists across routes but resets on reload)
  useEffect(() => {
    if (sessionPartyStateRef.current) {
      setPartyId(sessionPartyStateRef.current.partyId);
      setMeta(sessionPartyStateRef.current.meta);
      setPartyMembers(sessionPartyStateRef.current.members);
    }
    // no deps on purpose - run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleFetchParty = async (xuidOverride?: string) => {
    setIsLoading(true);
    // Clear current party info while fetching to avoid stale display
    setPartyId(null);
    setPartyMembers([]);
    setMeta({ partyId: null, visibility: 'unknown', memberCount: 0 });
    try {
      // 1) Determine current user's XUID from stored user info
      const userInfoRaw = localStorage.getItem('xbox_user_info');
      const xuid = userInfoRaw ? (JSON.parse(userInfoRaw)?.xid || JSON.parse(userInfoRaw)?.xuid) : null;
      const lookupXuid = xuidOverride || xuid;
      if (!lookupXuid) {
        console.error('Missing XUID in local storage');
        setIsLoading(false);
        return;
      }

      // 2) Fetch chat sessions for XUID
      const sessions = await xboxApiService.getChatSessions(String(lookupXuid));
      const results = sessions.data?.results || sessions.data?.value || [];
      if (!results || results.length === 0) {
        setPartyId(null);
        setPartyMembers([]);
        setMeta({ partyId: null, visibility: 'unknown', memberCount: 0 });
        // Friendly toast when no party is found
        if (xuidOverride) {
          const label = selectedText || xuidOverride;
          toast(`No active party found for ${label}`, 'info');
        } else {
          toast('No active party found for your account', 'info');
        }
        setIsLoading(false);
        return;
      }

      // Prefer an active/open session if available
      const active = results.find((r: any) => (r.status || '').toLowerCase() === 'active') || results[0];
      // Extract partyId (sessionRef.name)
      const partyIdFromSession = active?.sessionRef?.name || active?.sessionRef?.scid;
      if (!partyIdFromSession) {
        setPartyId(null);
        setPartyMembers([]);
        setIsLoading(false);
        return;
      }

      setPartyId(partyIdFromSession);

      // 3) Fetch party info by partyId
      const partyInfo = await xboxApiService.getPartyInfoById(partyIdFromSession);
      const data = partyInfo.data || {};

      // Members can be in an object keyed by numeric indices
      const membersObject = data.members && typeof data.members === 'object' && !Array.isArray(data.members)
        ? data.members
        : undefined;
      let membersArray: any[] = [];
      if (membersObject) {
        membersArray = Object.keys(membersObject)
          .sort((a, b) => Number(a) - Number(b))
          .map(k => membersObject[k]);
      } else if (Array.isArray(data.members)) {
        membersArray = data.members;
      }
      // Some responses also include a `users` array with gamertag/xuid
      const usersArray: any[] = Array.isArray(data.users) ? data.users : [];

      // Helper to find user by xuid to attach gamertag if missing
      const userByXuid = new Map<string, any>();
      usersArray.forEach(u => userByXuid.set(String(u.xuid), u));

      const creatorXuid: string | undefined = data.membersOnly?.bumblelionRelayCreator || data.membersOnly?.bumbleLionRelayCreator;
      const visibility: string = (data.constants?.system?.visibility || (data.open === true ? 'open' : data.open === false ? 'closed' : 'unknown')).toString().toLowerCase();

      // Decode relay descriptor to hostname
      const descriptorB64: string | undefined = data.membersOnly?.bumbleLionData?.descriptor || data.membersOnly?.bumblelion?.descriptor;
      const decodeDescriptor = (b64?: string): string | undefined => {
        if (!b64) return undefined;
        try {
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const text = new TextDecoder().decode(bytes);
          // Prefer hostnames starting explicitly with 'dns-'
          let m = text.match(/(dns-[a-z0-9-]+\.cloudapp\.azure\.com)/i);
          if (m && m[1]) return m[1].toLowerCase();
          // Fallback: extract the closest 'dns-' segment preceding cloudapp domain
          m = text.match(/dns-[a-z0-9-]+\.[a-z0-9.-]*cloudapp\.azure\.com/i);
          if (m && m[0]) return m[0].toLowerCase();
          return undefined;
        } catch {
          return undefined;
        }
      };
      const relayHost = decodeDescriptor(descriptorB64);

      // Build initial list
      let mappedMembers: PartyMember[] = (membersArray || []).map((m: any) => {
        const xuidVal = m?.constants?.system?.xuid || m?.xuid || m?.properties?.system?.xuid;
        const user = xuidVal ? userByXuid.get(String(xuidVal)) : undefined;
        return {
          gamertag: m?.gamertag || user?.gamertag || 'Unknown',
          xuid: String(xuidVal || user?.xuid || 'unknown'),
          isCreator: creatorXuid ? String(xuidVal) === String(creatorXuid) : false,
        };
      }).filter(mm => mm.xuid !== CRASH_XUID);

      // Enrich avatars via resolve-xuid (best-effort, non-blocking)
      try {
        const updates = await Promise.all(
          mappedMembers.map(async (mm) => {
            if (!mm.xuid || mm.xuid === 'unknown') return { xuid: mm.xuid, avatar: undefined };
            const prof = await xboxApiService.resolveXuid(mm.xuid);
            const pic = prof.data?.profileUsers?.[0]?.settings?.find((s: any) => s.id === 'GameDisplayPicRaw')?.value
              || prof.data?.profileUsers?.[0]?.settings?.find((s: any) => s.id === 'AppDisplayPicRaw')?.value;
            return { xuid: mm.xuid, avatar: pic };
          })
        );
        const picByXuid = new Map<string, string | undefined>();
        updates.forEach(u => picByXuid.set(u.xuid, u.avatar));
        mappedMembers = mappedMembers.map(mm => ({ ...mm, avatarUrl: picByXuid.get(mm.xuid) as any }));
      } catch {}

      setPartyMembers(mappedMembers);
      setMeta({
        partyId: partyIdFromSession,
        visibility,
        creatorXuid: creatorXuid,
        creatorGamertag: creatorXuid ? (userByXuid.get(String(creatorXuid))?.gamertag) : undefined,
        relayHost,
        memberCount: mappedMembers.length,
      });
      // Save to in-memory session cache (resets on reload)
      sessionPartyStateRef.current = {
        partyId: partyIdFromSession,
        meta: {
          partyId: partyIdFromSession,
          visibility,
          creatorXuid,
          creatorGamertag: creatorXuid ? (userByXuid.get(String(creatorXuid))?.gamertag) : undefined,
          relayHost,
          memberCount: mappedMembers.length,
        },
        members: mappedMembers,
      };
      // Success toast
      toast(`${xuidOverride ? "Friend's" : 'Your'} party loaded`, 'success');
    } catch (error) {
      console.error('Failed to fetch party:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCrashParty = async () => {
    try {
      if (!partyId) return;
      const res = await xboxApiService.crashPartyXbl(partyId);
      if (!res.error) {
        toast('Party crash initiated', 'success');
      } else {
        setPartyMembers(prev => prev.filter(m => m.xuid !== CRASH_XUID));
      }
    } catch (error) {
      console.error('Failed to crash party:', error);
      toast('Failed to crash party', 'error');
      setPartyMembers(prev => prev.filter(m => m.xuid !== CRASH_XUID));
    }
  };

  const handleReloadParty = async () => {
    if (!partyId) return;
    setIsLoading(true);
    try {
      const partyInfo = await xboxApiService.getPartyInfoById(partyId);
      const data = partyInfo.data || {};
      // parse members
      const membersObject = data.members && typeof data.members === 'object' && !Array.isArray(data.members)
        ? data.members
        : undefined;
      let membersArray: any[] = [];
      if (membersObject) {
        membersArray = Object.keys(membersObject).sort((a, b) => Number(a) - Number(b)).map(k => membersObject[k]);
      } else if (Array.isArray(data.members)) {
        membersArray = data.members;
      }
      const usersArray: any[] = Array.isArray(data.users) ? data.users : [];
      const userByXuid = new Map<string, any>();
      usersArray.forEach(u => userByXuid.set(String(u.xuid), u));
      const creatorXuid: string | undefined = data.membersOnly?.bumblelionRelayCreator || data.membersOnly?.bumbleLionRelayCreator;
      const visibility: string = (data.constants?.system?.visibility || (data.open === true ? 'open' : data.open === false ? 'closed' : 'unknown')).toString().toLowerCase();
      const descriptorB64: string | undefined = data.membersOnly?.bumbleLionData?.descriptor || data.membersOnly?.bumblelion?.descriptor;
      const decodeDescriptor = (b64?: string): string | undefined => {
        if (!b64) return undefined;
        try {
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const text = new TextDecoder().decode(bytes);
          let m = text.match(/(dns-[a-z0-9-]+\.cloudapp\.azure\.com)/i);
          if (m && m[1]) return m[1].toLowerCase();
          m = text.match(/dns-[a-z0-9-]+\.[a-z0-9.-]*cloudapp\.azure\.com/i);
          if (m && m[0]) return m[0].toLowerCase();
        } catch {}
        return undefined;
      };
      const relayHost = decodeDescriptor(descriptorB64);

      const mappedMembers: PartyMember[] = (membersArray || []).map((m: any) => {
        const xuidVal = m?.constants?.system?.xuid || m?.xuid || m?.properties?.system?.xuid;
        const user = xuidVal ? userByXuid.get(String(xuidVal)) : undefined;
        return {
          gamertag: m?.gamertag || user?.gamertag || 'Unknown',
          xuid: String(xuidVal || user?.xuid || 'unknown'),
          isCreator: creatorXuid ? String(xuidVal) === String(creatorXuid) : false,
        };
      }).filter(mm => mm.xuid !== CRASH_XUID);

      setPartyMembers(mappedMembers);
      setMeta(prev => ({
        partyId: partyId,
        visibility,
        creatorXuid,
        creatorGamertag: creatorXuid ? (userByXuid.get(String(creatorXuid))?.gamertag) : prev.creatorGamertag,
        relayHost,
        memberCount: mappedMembers.length,
      }));
      toast('Party reloaded', 'success');
    } catch (e) {
      console.error('Failed to reload party:', e);
      toast('Failed to reload party', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Toaster />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Party Chats</h1>
          <p className="text-muted-foreground mt-2">
            Fetch existing party information and manage party sessions
          </p>
        </div>
        
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-start lg:w-auto lg:justify-end">
          <Button onClick={() => handleFetchParty()} disabled={isLoading} className="flex items-center gap-2" title="Fetch my party">
            <Search className="h-4 w-4" />
            {isLoading ? 'Fetching...' : 'My Party'}
          </Button>
          <Button onClick={() => setFriendsModalOpen(true)} variant="secondary" className="flex items-center gap-2" title="Open friends list">
            <Search className="h-4 w-4" />
            Friends
          </Button>
          {partyId && (
            <Button onClick={handleReloadParty} variant="outline" className="flex items-center gap-2" title="Reload current party">
              <RefreshCw className="h-4 w-4" />
              Reload
            </Button>
          )}
          <Button onClick={handleCrashParty} variant="destructive" disabled={partyMembers.length === 0} className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Crash Party
          </Button>
        </div>
      </div>

      {/* Friends modal */}
      <Modal open={friendsModalOpen} onClose={() => setFriendsModalOpen(false)} title="Select a friend">
        <div className="space-y-3">
          <input
            value={selectedText}
            onChange={(e) => setSelectedText(e.target.value)}
            placeholder="Filter by gamertag..."
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="max-h-80 overflow-auto divide-y divide-border rounded-md border border-border">
            {friends
              .filter(f => f.gamertag.toLowerCase().includes(selectedText.toLowerCase()))
              .slice(0, 200)
              .map((f) => (
                <button
                  key={f.xuid}
                  onClick={() => {
                    setSelectedText('');
                    setFriendsModalOpen(false);
                    handleFetchParty(f.xuid);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-accent/50 flex items-center justify-between"
                >
                  <span className="truncate flex items-center gap-2">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${f.isOnline ? 'bg-green-500' : 'bg-zinc-600'}`} />
                    {f.gamertag}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">{f.xuid}</span>
                </button>
              ))}
            {friends.filter(f => f.gamertag.toLowerCase().includes(selectedText.toLowerCase())).length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</div>
            )}
          </div>
        </div>
      </Modal>

      <Card>
        <CardHeader>
          <CardTitle>Party Info</CardTitle>
          <CardDescription>Session details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs text-muted-foreground">Visibility</div>
              <div className="text-foreground font-medium capitalize">{meta.visibility === 'open' ? 'Open' : meta.visibility === 'closed' ? 'Closed' : 'Unknown'}</div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs text-muted-foreground">Party ID</div>
              <div className="text-foreground font-mono text-sm break-all">{meta.partyId || '-'}</div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs text-muted-foreground">Members</div>
              <div className="text-foreground font-medium">{meta.memberCount}</div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs text-muted-foreground">Creator</div>
              <div className="text-foreground font-medium">{meta.creatorGamertag || meta.creatorXuid || '-'}</div>
            </div>
            <div className="rounded-lg border border-border p-3 md:col-span-2">
              <div className="text-xs text-muted-foreground">Party Relay Server</div>
              <div className="text-foreground font-mono text-sm break-all">{meta.relayHost || '-'}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Party Members</CardTitle>
          <CardDescription>Current party members</CardDescription>
        </CardHeader>
        <CardContent>
          {partyMembers.length > 0 ? (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[48px]">Avatar</TableHead>
                    <TableHead>Gamertag</TableHead>
                    <TableHead>XUID</TableHead>
                    <TableHead>Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partyMembers.map((member, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={member.avatarUrl} alt={`${member.gamertag}'s avatar`} />
                          <AvatarFallback>{member.gamertag.slice(0,2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="font-medium">{member.gamertag}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono text-sm">{member.xuid}</TableCell>
                      <TableCell>{member.isCreator ? <span className="text-cyan-400">Creator</span> : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No party members found</p>
                <p className="text-sm">Click "Fetch Party" to load party information</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartyChats;
